"use client";

import { InfiniteScroll } from "@cmp-src/components/InfiniteScroll/InfiniteScroll";
import { useFormControlInputA11y } from "@cmp-src/form/FormControlWrapper/FormControlWrapperProvider";
import { TextInput } from "@cmp-src/form/TextInput";
import { SearchIcon } from "@cmp-src/icons/icons";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import { ModalButtons } from "@cmp-src/overlay/ModalButtons/ModalButtons";
import { Popover } from "@cmp-src/overlay/Popover/Popover";
import { buttonVariants } from "@cmp-src/primitives/button";
import { Checkbox } from "@cmp-src/primitives/checkbox";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";
import { cn, useDisclosure } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { Slot } from "@radix-ui/react-slot";
import Fuse from "fuse.js";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2,
  XIcon
} from "lucide-react";
import React from "react";
import type { SelectItem, UnifiedSelectProps } from "./types";

// Helper to get all leaf (non-parent) descendants of an item
function getLeafDescendants<T>(item: SelectItem<T>): SelectItem<T>[] {
  if (!item.children || item.children.length === 0) {
    return [item];
  }
  return item.children.flatMap((child) => getLeafDescendants(child));
}

/**
 * The leaves under an item that a selectable parent may toggle: everything
 * {@link getLeafDescendants} finds, minus the ones the caller disabled.
 */
function getSelectableLeafDescendants<T>(
  item: SelectItem<T>
): SelectItem<T>[] {
  return getLeafDescendants(item).filter((leaf) => !leaf.disabled);
}

// Helper to flatten items for search
function flattenItems<T>(
  items: SelectItem<T>[],
  parent?: SelectItem<T>
): (SelectItem<T> & { parent?: SelectItem<T> })[] {
  return items.reduce<(SelectItem<T> & { parent?: SelectItem<T> })[]>(
    (acc, item) => {
      const itemWithParent = { ...item, parent };
      acc.push(itemWithParent);
      if (item.children) {
        acc.push(...flattenItems(item.children, item));
      }
      return acc;
    },
    []
  );
}

// Helper to find path to an item
function findItemPath<T>(
  itemId: string,
  items: SelectItem<T>[],
  path: SelectItem<T>[] = []
): SelectItem<T>[] {
  for (const item of items) {
    if (item.id === itemId) {
      return path;
    }
    if (item.children) {
      const found = findItemPath(itemId, item.children, [...path, item]);
      if (found.length > 0 || item.children.some((c) => c.id === itemId)) {
        return found.length > 0 ? found : [...path, item];
      }
    }
  }
  return [];
}

// Helper to get parent names for display
function getItemParentPath<T>(itemId: string, items: SelectItem<T>[]): string {
  const path = findItemPath(itemId, items);
  if (path.length === 0) return "";
  return path.map((p) => p.name).join(" → ");
}

/**
 * The select behind most pickers in Tactic: single or multiple choice, local or server-side
 * search, nested levels, infinite scroll. Opens a popover on desktop and a drawer below `md`.
 * `value` / `values` are whole `SelectItem`s; inside a form use `UnifiedSelectInput` (or
 * `HookFormUnifiedSelectInput`), which works with item ids and adds label, help and errors.
 */
export function UnifiedSelect<T = unknown>({
  items,
  value,
  values = [],
  onSelect,
  onSelectMultiple,
  searchable = false,
  searchPlaceholder = "Search...",
  hierarchical = false,
  multiselect = false,
  withApply = false,
  splitView = false,
  variant = "default",
  placeholder = "Select...",
  trigger,
  triggerIcon,
  triggerClassName,
  contentClassName,
  disabled = false,
  label,
  name,
  renderItem,
  sectionLabel,
  footerVariant = "buttons",
  listVariant = "default",
  renderValue,
  contentHeight = "250px",
  contentWidth = "280px",
  minWidth = 80,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  onSearchChange,
  isSearching = false,
  emptyMessage = "No items found",
  maxDisplayItems,
  selectableParents = false
}: UnifiedSelectProps<T>) {
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  // Inside a form field the trigger is described by its helper text and error
  const { "aria-describedby": ariaDescribedBy } = useFormControlInputA11y();
  const popoverDisclosure = useDisclosure();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());
  const contentRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const closeResetTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const [searchTerm, setSearchTerm] = React.useState("");
  const [navigationStack, setNavigationStack] = React.useState<SelectItem<T>[]>(
    []
  );
  const [pendingSelections, setPendingSelections] =
    React.useState<SelectItem<T>[]>(values);
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);

  // Handle search term changes
  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    onSearchChange?.(newSearchTerm);
  };

  // Reset pending selections when values prop changes
  const valuesKey = values.map((v) => v.id).join(",");
  React.useEffect(() => {
    setPendingSelections(values);
  }, [valuesKey]);

  React.useEffect(() => {
    return () => {
      if (closeResetTimeoutRef.current) {
        clearTimeout(closeResetTimeoutRef.current);
      }
    };
  }, []);

  // Create Fuse instance for search
  const fuse = React.useMemo(() => {
    const allItems = flattenItems(items);
    return new Fuse(allItems, {
      keys: ["name", "keywords"],
      threshold: 0.3,
      distance: 100
    });
  }, [items]);

  // Get current items to display
  const currentItems = React.useMemo(() => {
    if (searchTerm && searchable) {
      // Server-driven search: the caller already filtered `items` for this
      // term via onSearchChange — render them as-is. Re-running Fuse on top
      // would filter stale (previous-term) items against the new term, and
      // could drop valid server matches that miss Fuse's own threshold.
      if (onSearchChange) {
        return hierarchical ? items.filter((item) => !item.children) : items;
      }
      const results = fuse.search(searchTerm);
      // Filter out parent items if not hierarchical, only show leaf items
      // Also deduplicate by item ID to prevent duplicates in search results
      const seen = new Set<string>();
      return results
        .map((result) => result.item)
        .filter((item) => !hierarchical || !item.children)
        .filter((item) => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
    }
    if (hierarchical && navigationStack.length > 0) {
      const currentParent = navigationStack[navigationStack.length - 1];
      return currentParent.children || [];
    }
    return items;
  }, [
    items,
    searchTerm,
    searchable,
    fuse,
    hierarchical,
    navigationStack,
    onSearchChange
  ]);

  const handleSelect = (item: SelectItem<T>) => {
    // While a server-driven search is still in flight, the rendered items are
    // last round's results (or an earlier page), not matches for what's
    // currently typed — picking one now would silently select the wrong
    // entry. "Searching…" is shown for the same reason; keep the list inert
    // until it catches up.
    if (isSearching) return;

    // Blur search input when selecting an item
    if (searchable && searchInputRef.current) {
      searchInputRef.current.blur();
      contentRef.current?.focus();
    }

    // If hierarchical and item has children, navigate into it
    if (hierarchical && item.children && item.children.length > 0) {
      if (searchTerm) {
        const path = findItemPath(item.id, items);
        setNavigationStack([...path, item]);
        setSearchTerm("");
      } else {
        setNavigationStack([...navigationStack, item]);
      }
      return;
    }

    if (multiselect) {
      const isSelected = pendingSelections.some((s) => s.id === item.id);
      const newSelections = isSelected
        ? pendingSelections.filter((s) => s.id !== item.id)
        : [...pendingSelections, item];

      setPendingSelections(newSelections);

      if (!withApply) {
        onSelectMultiple?.(newSelections);
      }
    } else {
      onSelect?.(item);
      popoverDisclosure.close();
    }
  };

  const handleApply = () => {
    onSelectMultiple?.(pendingSelections);
    popoverDisclosure.close();
  };

  const handleClear = () => {
    setPendingSelections([]);
    if (!withApply) {
      onSelectMultiple?.([]);
    }
  };

  const handleSelectParent = (item: SelectItem<T>) => {
    // A disabled leaf cannot be picked on its own, so selecting its parent must
    // not pick it either — otherwise the group checkbox is a way around the
    // very restriction the caller expressed.
    const leaves = getSelectableLeafDescendants(item);
    const leafIds = new Set(leaves.map((l) => l.id));
    const allSelected =
      leaves.length > 0 &&
      leaves.every((l) => pendingSelections.some((s) => s.id === l.id));

    let newSelections: SelectItem<T>[];
    if (allSelected) {
      newSelections = pendingSelections.filter((s) => !leafIds.has(s.id));
    } else {
      const existing = pendingSelections.filter((s) => !leafIds.has(s.id));
      newSelections = [...existing, ...leaves];
    }

    setPendingSelections(newSelections);
    if (!withApply) {
      onSelectMultiple?.(newSelections);
    }
  };

  const getParentSelectionState = (
    item: SelectItem<T>
  ): "none" | "partial" | "all" => {
    // Counted over the leaves that can actually be picked, so a group whose
    // every selectable office is chosen reads as fully selected rather than
    // being held at "partial" forever by a disabled sibling.
    const leaves = getSelectableLeafDescendants(item);
    if (leaves.length === 0) return "none";
    const selectedCount = leaves.filter((l) =>
      pendingSelections.some((s) => s.id === l.id)
    ).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === leaves.length) return "all";
    return "partial";
  };

  const handleBack = () => {
    setNavigationStack(navigationStack.slice(0, -1));
  };

  const handleOpenChange = (open: boolean) => {
    // Prevent opening when disabled
    if (open && disabled) {
      return;
    }
    popoverDisclosure.onOpenChange(open);
    if (open) {
      // Reopening before the close-reset timeout fired — cancel it so it
      // doesn't clear the search out from under the now-open select.
      if (closeResetTimeoutRef.current) {
        clearTimeout(closeResetTimeoutRef.current);
        closeResetTimeoutRef.current = null;
      }
      // Reset focused index when opening
      setFocusedIndex(-1);
      // Find the index of the currently selected item to focus it
      if (value && !multiselect) {
        const selectedIndex = currentItems.findIndex(
          (item) => item.id === value.id
        );
        if (selectedIndex >= 0) {
          setFocusedIndex(selectedIndex);
        }
      }
    } else {
      closeResetTimeoutRef.current = setTimeout(() => {
        closeResetTimeoutRef.current = null;
        setNavigationStack([]);
        setSearchTerm("");
        // Clear the caller's search state too — otherwise a server-backed
        // search (via onSearchChange) stays filtered by the previous term
        // even though the input shows empty on reopen.
        onSearchChange?.("");
        setFocusedIndex(-1);
        // Note: Don't reset pendingSelections here - the useEffect on valuesKey handles syncing
        // Resetting here with stale `values` would override the parent's update
      }, 200);
    }
  };

  // Scroll focused item into view
  React.useEffect(() => {
    if (focusedIndex >= 0 && currentItems[focusedIndex]) {
      const itemEl = itemRefs.current.get(currentItems[focusedIndex].id);
      if (itemEl) {
        itemEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex, currentItems]);

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Check if the event is coming from the search input
    const isFromInput = (e.target as HTMLElement)?.tagName === "INPUT";

    if (!popoverDisclosure.isOpen) {
      // Prevent opening when disabled
      if (disabled) {
        return;
      }
      // Open on arrow down or enter when closed
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        popoverDisclosure.open();
      }
      return;
    }

    const itemCount = currentItems.length;
    const hasBackButton =
      hierarchical && navigationStack.length > 0 && !searchTerm;

    // Handle hierarchical navigation before checking defaultPrevented
    // This allows ArrowRight/ArrowLeft to work even if input handled the event
    if (e.key === "ArrowRight" && hierarchical) {
      if (focusedIndex >= 0 && focusedIndex < itemCount) {
        const focusedItem = currentItems[focusedIndex];
        if (focusedItem.children && focusedItem.children.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          handleSelect(focusedItem);
          return;
        }
      }
    }

    if (e.key === "ArrowLeft" && hasBackButton) {
      e.preventDefault();
      e.stopPropagation();
      handleBack();
      return;
    }

    // Prevent double handling from event bubbling
    if (e.defaultPrevented) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex((prev) => {
          const next = prev + 1;
          return next >= itemCount ? 0 : next;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex((prev) => {
          const next = prev - 1;
          return next < 0 ? itemCount - 1 : next;
        });
        break;
      case "Home":
        // Allow Home key in input for cursor navigation
        if (isFromInput) return;
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex(0);
        break;
      case "End":
        // Allow End key in input for cursor navigation
        if (isFromInput) return;
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex(itemCount - 1);
        break;
      case "Enter":
        e.preventDefault();
        e.stopPropagation();
        if (focusedIndex >= 0 && focusedIndex < itemCount) {
          handleSelect(currentItems[focusedIndex]);
        }
        break;
      case " ":
        // Allow space in input for typing
        if (isFromInput) return;
        e.preventDefault();
        e.stopPropagation();
        if (focusedIndex >= 0 && focusedIndex < itemCount) {
          handleSelect(currentItems[focusedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        popoverDisclosure.close();
        break;
      case "Backspace":
        // Allow Backspace in input for editing
        if (isFromInput) return;
        // Go back in hierarchy if at start of empty search
        if (hasBackButton && searchTerm === "") {
          e.preventDefault();
          e.stopPropagation();
          handleBack();
        }
        break;
      case "ArrowLeft":
        // Allow ArrowLeft in input for cursor navigation
        // (hierarchical back navigation is handled before defaultPrevented check)
        if (isFromInput) return;
        break;
      case "ArrowRight":
        // Allow ArrowRight in input for cursor navigation
        // (hierarchical forward navigation is handled before defaultPrevented check)
        if (isFromInput) return;
        break;
      default:
        // If searchable and user types a letter/number, focus the search input
        if (
          searchable &&
          !isFromInput &&
          e.key.length === 1 &&
          !e.ctrlKey &&
          !e.metaKey &&
          !e.altKey
        ) {
          searchInputRef.current?.focus();
        }
        break;
    }
  };

  // Reset focused index when items change
  React.useEffect(() => {
    setFocusedIndex(-1);
  }, [searchTerm, navigationStack]);

  const isItemSelected = (item: SelectItem<T>) => {
    if (multiselect) {
      return pendingSelections.some((s) => s.id === item.id);
    }
    return value?.id === item.id;
  };

  // Render trigger content
  const renderTriggerContent = () => {
    if (renderValue) {
      const rendered = renderValue(
        multiselect ? (values.length > 0 ? values : null) : (value ?? null)
      );
      // A custom renderer with nothing to show falls through to the
      // placeholder: an empty trigger reads as a broken control, not as an
      // empty one.
      if (rendered !== null && rendered !== undefined) return rendered;
    }

    if (multiselect && values.length > 0) {
      if (maxDisplayItems !== undefined) {
        const displayItems = values.slice(0, maxDisplayItems);
        const remainingCount = values.length - maxDisplayItems;
        const displayText = displayItems.map((v) => v.name).join(", ");
        if (remainingCount > 0) {
          return `${displayText} +${remainingCount}`;
        }
        return displayText;
      }
      if (values.length === 1) {
        return values[0].name;
      }
      return `${values.length} selected`;
    }

    if (value) {
      return value.name;
    }

    return <span className="text-content">{placeholder}</span>;
  };

  const hasSelection = multiselect ? values.length > 0 : Boolean(value);

  // Rendered as a styled span, not a <button>: both the mobile wrapper and
  // the desktop PopoverTrigger already render the interactive <button>, and
  // nesting buttons is invalid HTML (hydration error in React 19).
  const pillTriggerContent = (
    <span
      className={cn(
        "inline-flex h-8 max-w-full items-center gap-1.75 rounded-full border pr-3 pl-2.75 font-inter font-medium text-control transition-colors [&_svg]:shrink-0",
        hasSelection
          ? "border-primary bg-neutral-50 text-primary"
          : "border-neutral-200 bg-white text-content-strong hover:bg-neutral-50",
        disabled && "pointer-events-none opacity-50",
        triggerClassName
      )}
    >
      {triggerIcon}
      {label && <span className="sr-only">{label}: </span>}
      <span className="min-w-0 truncate">{renderTriggerContent()}</span>
      <ChevronDownIcon className="size-3.5 opacity-65" />
    </span>
  );

  const defaultTriggerContent = (
    <span
      className={cn(
        buttonVariants({ variant: variant === "ghost" ? "ghost" : "outline" }),
        "flex relative min-w-0 max-w-full items-center font-normal",
        variant !== "ghost" && "w-full",
        variant === "ghost" && "border-0 shadow-none hover:bg-accent/50",
        disabled && "pointer-events-none opacity-50",
        triggerClassName
      )}
    >
      <span className="flex h-full w-full items-center justify-between gap-2">
        <span className="min-w-0 truncate flex-1 text-left">
          <span className={"absolute inset-2.5 mr-8 truncate !font-inter"}>
            {/* The trigger is named by its content: prefix the field label for screen readers */}
            {label && <span className="sr-only">{label}: </span>}
            {renderTriggerContent()}
          </span>
        </span>
        <span>
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </span>
      </span>
    </span>
  );

  const triggerContent =
    trigger ||
    (variant === "pill" ? pillTriggerContent : defaultTriggerContent);

  // Selected items panel for split view
  const selectedPanel = multiselect && splitView && (
    <div className="flex flex-col border-l border-neutral-100 bg-neutral-50/50 w-[160px]">
      {searchable && (
        <div className="border-b border-neutral-100 p-1.5">
          <div className="px-3 h-5 py-3 flex items-center">
            <span className="text-xs text-content">
              <span className="font-bold">{pendingSelections.length}</span>{" "}
              Selected
            </span>
          </div>
        </div>
      )}
      {!searchable && (
        <div className="border-b border-neutral-100 px-3 py-2 text-xs text-content">
          <span className="font-bold">{pendingSelections.length}</span> Selected
        </div>
      )}
      <ScrollArea
        style={{ maxHeight: contentHeight }}
        className="w-full overflow-y-auto"
      >
        <div className="py-1">
          {pendingSelections.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-content">
              No items selected
            </div>
          ) : (
            pendingSelections.map((item) => {
              const parentPath = hierarchical
                ? getItemParentPath(item.id, items)
                : "";
              return (
                <button
                  key={item.id}
                  type="button"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => handleSelect(item)}
                  className="group flex w-full items-center gap-2 px-3 py-2 hover:bg-accent/50 max-w-full text-left cursor-pointer"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate max-w-[110px]">
                      {item.name}
                    </div>
                    {parentPath && (
                      <div className="text-xs text-content truncate max-w-[110px]">
                        {parentPath}
                      </div>
                    )}
                  </div>
                  <span className="shrink-0 p-0.5 text-content group-hover:text-foreground">
                    <XIcon className="h-3.5 w-3.5" />
                  </span>
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );

  const mainContent = (
    <div className="flex-1 min-w-0 overflow-hidden">
      {searchable && (
        <div className="border-b border-neutral-100 p-0.5">
          <TextInput
            ref={searchInputRef}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label={searchPlaceholder}
            className="w-full"
            wrapperClassName="border-0 shadow-none px-3"
            inputClassName="h-5 py-3 outline-none"
            leftIcon={<SearchIcon className="size-4 mr-2" />}
          />
        </div>
      )}

      <ScrollArea
        ref={scrollRef}
        style={{ maxHeight: contentHeight }}
        className="w-full overflow-y-auto [&>div>div]:!min-w-0 [&>div>div]:!block"
      >
        {/* Back button for hierarchical navigation */}
        {hierarchical && navigationStack.length > 0 && !searchTerm && (
          <button
            type="button"
            onClick={handleBack}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            <span className="font-medium">
              {navigationStack.length > 1
                ? navigationStack[navigationStack.length - 2].name
                : "Back"}
            </span>
          </button>
        )}

        {/* Current level header for hierarchical */}
        {hierarchical && navigationStack.length > 0 && !searchTerm && (
          <div className="border-b border-neutral-100 px-3 py-2 text-xs font-medium text-content">
            {navigationStack[navigationStack.length - 1].name}
          </div>
        )}

        {sectionLabel && (
          <div
            className={cn(
              "px-3.5 font-semibold text-content text-section-label uppercase tracking-wider",
              listVariant === "inset" ? "pt-2.75 pb-2.25" : "pt-2.5 pb-1.5"
            )}
          >
            {sectionLabel}
          </div>
        )}

        {/* Items list */}
        <div className={listVariant === "inset" ? "px-1.5 pb-1.5" : "py-1"}>
          {currentItems.map((item, index) => {
            const isSelected = isItemSelected(item);
            const hasChildren =
              hierarchical && item.children && item.children.length > 0;
            const isFocused = focusedIndex === index;
            const isSelectableParent =
              selectableParents && multiselect && hasChildren;
            const parentState = isSelectableParent
              ? getParentSelectionState(item)
              : "none";
            // A custom renderItem owns the whole label slot, description included.
            const hasDescription = !renderItem && Boolean(item.description);

            const row = (
              <button
                key={item.id}
                ref={(el) => {
                  if (el) {
                    itemRefs.current.set(item.id, el);
                  } else {
                    itemRefs.current.delete(item.id);
                  }
                }}
                type="button"
                aria-pressed={hasChildren ? undefined : isSelected}
                disabled={item.disabled || isSearching}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setFocusedIndex(index)}
                onFocus={() => {
                  // Update focused index when button receives focus
                  setFocusedIndex(index);
                }}
                // Roving tabindex: one row stays reachable with Tab, the first until one is focused
                tabIndex={
                  isFocused || (focusedIndex === -1 && index === 0) ? 0 : -1
                }
                className={cn(
                  "flex w-full items-center overflow-hidden text-left text-sm transition-colors",
                  listVariant === "inset"
                    ? "gap-2.75 rounded-lg px-2.25 py-1.75"
                    : "gap-2 px-3 py-2",
                  "text-foreground",
                  "hover:bg-accent hover:text-accent-foreground",
                  "focus:bg-accent focus:text-accent-foreground focus:outline-none",
                  (item.disabled || isSearching) && "cursor-not-allowed opacity-50",
                  isSelected && !hasChildren && !multiselect && "bg-accent/50",
                  isFocused && "bg-accent text-accent-foreground",
                  isSelectableParent && "pl-9"
                )}
              >
                {multiselect && !hasChildren && (
                  // Only a picture of a checkbox: a real one would be a control
                  // nested in the row button, and the row already exposes aria-pressed
                  <span
                    aria-hidden
                    className={cn(
                      "flex shrink-0 items-center justify-center border border-neutral-400",
                      listVariant === "inset"
                        ? "size-5 rounded-[6px]"
                        : "size-4 rounded-[4px]",
                      isSelected && "border-none bg-black text-white"
                    )}
                  >
                    {isSelected && (
                      <CheckIcon
                        className={
                          listVariant === "inset" ? "size-3.5" : "size-4"
                        }
                      />
                    )}
                  </span>
                )}
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                <span
                  className={cn(
                    "min-w-0 flex-1",
                    !hasDescription && "truncate"
                  )}
                >
                  {renderItem ? (
                    renderItem(item, isSelected)
                  ) : hasDescription ? (
                    <>
                      <span className="block truncate">{item.name}</span>
                      <span className="block truncate text-xs text-content">
                        {item.description}
                      </span>
                    </>
                  ) : (
                    item.name
                  )}
                </span>
                {hasChildren ? (
                  <ChevronRightIcon className="h-4 w-4 shrink-0 opacity-50" />
                ) : isSelected && !multiselect ? (
                  <CheckIcon className="h-4 w-4 shrink-0 text-primary" />
                ) : null}
              </button>
            );

            if (!isSelectableParent) return row;

            // The group checkbox sits beside the row, not inside it: the row
            // navigates into the group, the checkbox toggles all its leaves
            return (
              <div key={item.id} className="relative">
                {row}
                <span className="absolute inset-y-0 left-3 flex items-center">
                  <Checkbox
                    checked={parentState !== "none"}
                    onCheckedChange={() => handleSelectParent(item)}
                    disabled={item.disabled || isSearching}
                    aria-label={`Select all in ${item.name}`}
                  />
                </span>
              </div>
            );
          })}

          {currentItems.length === 0 &&
            !isFetchingNextPage &&
            !isSearching && (
              <div className="px-3 py-6 text-center text-sm text-content">
                {emptyMessage}
              </div>
            )}

          {/* Shown alongside any stale results still on screen, not just
              when the list is empty — otherwise a pending search behind
              already-loaded items never signals that it's still running. */}
          {isSearching && (
            <div className="px-3 py-3 text-center text-xs text-content">
              Searching…
            </div>
          )}

          {/* Infinite scroll sentinel */}
          {hasNextPage && fetchNextPage && (
            <InfiniteScroll
              scrollRef={scrollRef}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage ?? false}
              offset={100}
            />
          )}
        </div>
      </ScrollArea>

      {/* Footer with Apply/Clear buttons for multiselect with withApply */}
      {multiselect && withApply && footerVariant === "compact" && (
        <div className="flex select-none items-center justify-between border-t border-neutral-100 px-3.5 py-2.5">
          <button
            type="button"
            onClick={handleClear}
            className="cursor-pointer font-semibold text-content text-row-subtitle uppercase tracking-wide hover:text-content-strong"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="cursor-pointer font-semibold text-brand text-row-subtitle uppercase tracking-wide"
          >
            Done
          </button>
        </div>
      )}
      {multiselect && withApply && footerVariant === "buttons" && (
        <div className="flex h-[44px] select-none items-center border-t border-neutral-100">
          <ModalButtons onApply={handleApply} onCancel={handleClear} />
        </div>
      )}
    </div>
  );

  const content = (
    <div
      ref={contentRef}
      className={cn(
        "w-full bg-white flex overflow-hidden outline-none",
        contentClassName
      )}
      tabIndex={-1}
    >
      {mainContent}
      {selectedPanel}
    </div>
  );

  // Focus the content when popover opens
  React.useEffect(() => {
    if (popoverDisclosure.isOpen && contentRef.current) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        contentRef.current?.focus();
      }, 50);
    }
  }, [popoverDisclosure.isOpen]);

  // Callers pass either a control (a <button> or a component rendering one) or
  // plain markup such as a <span> that only draws the trigger. A control must
  // not be wrapped in another button; plain markup needs button semantics.
  const isTriggerMarkupOnly =
    !trigger ||
    (React.isValidElement(trigger) &&
      typeof trigger.type === "string" &&
      trigger.type !== "button");

  if (!isMd) {
    const MobileTrigger = isTriggerMarkupOnly ? "button" : Slot;
    return (
      <>
        <MobileTrigger
          type="button"
          name={name}
          onClick={() => !disabled && popoverDisclosure.toggle()}
          onKeyDown={handleKeyDown}
          aria-describedby={ariaDescribedBy}
          className={cn(
            isTriggerMarkupOnly && "group inline-flex min-w-0 text-left w-full",
            disabled && "cursor-not-allowed opacity-50"
          )}
          disabled={disabled}
        >
          {triggerContent}
        </MobileTrigger>
        <MobileDrawer
          isOpen={popoverDisclosure.isOpen}
          onOpenChange={handleOpenChange}
          ariaLabel={label ?? placeholder}
        >
          {content}
        </MobileDrawer>
      </>
    );
  }

  return (
    <Popover
      bodyProps={{ className: "p-0 w-full" }}
      contentProps={{
        className: "overflow-hidden",
        "aria-label": label ?? placeholder,
        align: "start",
        style: {
          width: contentWidth,
          maxWidth: contentWidth,
          minWidth:
            parseInt(contentWidth || "0", 10) < minWidth
              ? minWidth + "px"
              : contentWidth
        },
        onKeyDown: handleKeyDown
      }}
      content={content}
      open={popoverDisclosure.isOpen}
      onOpenChange={handleOpenChange}
      triggerAsChild={!!trigger}
      triggerProps={{
        "aria-describedby": ariaDescribedBy,
        // Plain markup becomes the trigger through asChild: make it a keyboard-operable button
        ...(trigger &&
          isTriggerMarkupOnly && {
            role: "button",
            tabIndex: disabled ? -1 : 0,
            "aria-disabled": disabled || undefined,
            onKeyDown: handleKeyDown
          })
      }}
      // A pill sits inline at its own width, not stretched like a field.
      triggerClassname={
        trigger ? "" : variant === "pill" ? "w-auto" : undefined
      }
    >
      {triggerContent}
    </Popover>
  );
}
